import { useQuery } from '@tanstack/react-query';
import { fetchActiveJobsByWorker } from '@company/services/job/api';
import QueryKeys from '@constants/queryKeys';
import { MINUTE } from '@constants/various';

interface UseActiveJobsByWorkerParams {
  workerId: number;
  enabled?: boolean;
}

/**
 * React Query hook for fetching active jobs for a specific worker.
 * Provides caching, loading states, and automatic refetching.
 * Use this when you need to display jobs with loading/error states.
 *
 * @param workerId - The ID of the worker to fetch active jobs for
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns React Query result with data, isLoading, error, etc.
 */
export function useActiveJobsByWorker({
  workerId,
  enabled = true,
}: UseActiveJobsByWorkerParams) {
  return useQuery({
    queryKey: [QueryKeys.COMPANY_JOBS, 'active', workerId],
    queryFn: () => fetchActiveJobsByWorker(workerId),
    enabled: enabled && !!workerId,
    staleTime: 5 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

export default useActiveJobsByWorker;
